import MessageBroker from '@Services/MessageBroker';
import RequestUrls from '@Constants/RequestUrlsConstants';
import AppStorage from '@Services/AppStorage';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import clone from 'lodash/clone';
import omit from 'lodash/omit';
import cloneDeep from 'lodash/cloneDeep';
import extend from 'lodash/extend';
import isUndefined from 'lodash/isUndefined';
import * as BrowserHelper from '@Services/BrowserHelper';
import store from '@Store';
import { currentUserUpdated } from '@Containers/Header/CurrentUserActions';
import { updateUserDetails } from '@Containers/UserDetails/UserDetailsActions';
import PendoService from '@Services/Pendo';
import Catalyst from '@Services/Catalyst';
import NewRelicService from '@Services/NewRelicService';
import { FEATURES } from '@Constants/rx-ui/constants';
import { PENDO_FEEDBACK_URL, PENDO_URL } from '@Constants/BuildInfo';
import AuthUser from '@Services/Auth/AuthUser';
import { updatedUserConstants } from './UpdateUserConstants';
import * as AuthHelper from './AuthHelper';

export const getClientInfo = () => MessageBroker.send(RequestUrls.clientInfo, null, { Authorization: false });

export const getUser = () => AuthHelper.getUser();

export const _getGDPRSafeProps = (user) => {
  const blackListProps = ['email', 'firstName', 'lastName', 'username', 'loginDetails', 'photoUrl', 'devOptions'];
  const result = clone(user);
  result.details = omit(result.details, blackListProps);
  return result;
};

export const isFeatureEnabled = (feature) => AuthHelper.isFeatureEnabled(feature);

// Duplicated from Auth.js to break cyclic dependency
export const updateUser = (user, force) => {
  const features = !isEmpty(get(user, 'details.features', null));
  const oldPageSize = get(AuthUser.currentUser, 'details.settings.pageSize');
  let newPageSize = get(user, 'details.settings.pageSize', false);

  if (newPageSize && oldPageSize !== newPageSize) {
    // for IE default page size value is 15 and max value is 30, so if user has >=30 items in preferences -> set 30
    // otherwise set page size value from user's preferences
    if (BrowserHelper.detectOldIE() && get(AuthUser.currentUser, 'devOptions.resultsPerPageIELimit', true)) {
      newPageSize = newPageSize > 15 ? 30 : newPageSize;
    }
    AppStorage.instance.set('resultsPerPage', newPageSize);
  }

  if (force) {
    AuthUser.currentUser = cloneDeep(user);
  } else {
    extend(AuthUser.currentUser, user);
  }

  if (features) {
    AuthUser.currentUser.devOptions = Object.keys(FEATURES)?.reduce((acc, key) => {
      acc[key] = isFeatureEnabled(FEATURES[key]);
      return acc;
    }, {});
  }

  AppStorage.instance.set('currentUser', _getGDPRSafeProps(AuthUser.currentUser));

  // Update in React Redux
  store.dispatch(currentUserUpdated(AuthUser.currentUser));
  store.dispatch(updateUserDetails(AuthUser.currentUser.details));

  const disableTracking = get(AuthUser.currentUser, 'details.disableTracking', {});
  if (!isEmpty(disableTracking)) {
    const pendoFeedbackEnabled = isFeatureEnabled('PendoFeedbackTrial');
    Catalyst.refreshVisitorData(force);

    if (!isUndefined(disableTracking.ADOBE_ANALYTICS)) {
      Catalyst.setNoTracking(disableTracking.ADOBE_ANALYTICS);
    }
    if (!isUndefined(disableTracking.ADOBE_ANALYTICS)) {
      // should be NEW_RELIC
      NewRelicService.setNoTracking(disableTracking.ADOBE_ANALYTICS);
    }

    // Pendo tracking
    const userId = get(AuthUser.currentUser, 'details.loginDetails.userId');
    if (!isUndefined(disableTracking.PENDO) && userId) {
      if (disableTracking.PENDO) {
        // stop tracking and guides after login if feature disabled
        PendoService.disablePendo();
      }

      const url = pendoFeedbackEnabled ? PENDO_FEEDBACK_URL : PENDO_URL;
      PendoService.setNoTracking(disableTracking.PENDO);
      PendoService.link(url)
        .then(() => PendoService.updateVisitor(AuthUser.currentUser))
        .catch(Logger.warn);
    }
  }
  // eslint-disable-next-line no-unused-expressions
  updatedUserConstants && updatedUserConstants.updatedUserDefer && updatedUserConstants.updatedUserDefer.resolve(AuthUser.currentUser);
  return AuthUser.currentUser;
};

export const isAnonymousUser = () => AuthHelper.isAnonymousUser();
